import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

interface RechargeHistoryItem {
  id: number;
  user_id: number;
  profile_id?: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  amount: number;
  transaction_id: string;
  payment_method: string;
  payment_date: string;
  status: string;
  admin_notes?: string;
  rejection_reason?: string;
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
}

const AdminRechargeHistoryPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState<RechargeHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [hasNext, setHasNext] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const getStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' => {
    if (status === 'APPROVED') return 'success';
    if (status === 'PENDING') return 'warning';
    if (status === 'REJECTED') return 'error';
    return 'default';
  };

  const fetchHistory = async (cursorToken: string | null, append: boolean) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else if (!items.length) {
        setLoading(true);
      }
      const params: Record<string, any> = {
        page_size: rowsPerPage,
      };
      if (cursorToken) {
        params.cursor = cursorToken;
      } else {
        params.page = 1;
      }

      if (search.trim()) params.search = search.trim();
      if (statusFilter !== 'ALL') params.recharge_status = statusFilter;
      if (minAmount.trim()) params.min_amount = Number(minAmount);
      if (maxAmount.trim()) params.max_amount = Number(maxAmount);
      if (paymentMethod.trim()) params.payment_method = paymentMethod.trim();
      if (fromDate) params.from_date = new Date(fromDate).toISOString();
      if (toDate) params.to_date = new Date(`${toDate}T23:59:59`).toISOString();

      const response = await api.get('/admin/payment-gateway/recharge-history', {
        params,
        timeout: 15000,
      });
      const rows = response.data.data || [];
      setItems((prev) => (append ? [...prev, ...rows] : rows));
      setTotal(response.data.total || 0);
      setHasNext(Boolean(response.data.has_next));
      setNextCursor(response.data.next_cursor || null);
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Recharge history request timed out. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load recharge history');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchHistory(null, false);
  }, [rowsPerPage]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchHistory(null, false);
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [search, statusFilter, minAmount, maxAmount, paymentMethod, fromDate, toDate]);

  return (
    <AdminLayout currentPage="recharge-history">
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #7b0011 0%, #a21d31 55%, #f3dcc3 100%)',
            borderRadius: 4,
            p: { xs: 2.5, md: 3 },
            color: 'white',
            boxShadow: '0 12px 30px rgba(78, 0, 11, 0.18)',
            mb: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
            Recharge History
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 760, color: 'rgba(255,255,255,0.92)' }}>
            Track recharge approvals in a cleaner card layout with profile ID, amount, payment method, and status.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(255,255,255,0.88)' }}>
            Showing {items.length} of {total} records
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 2, borderRadius: 4, border: '1px solid #eddac2', boxShadow: '0 8px 24px rgba(78, 0, 11, 0.08)' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 0.5fr 0.5fr 0.8fr 0.8fr 0.8fr 140px' }, gap: 1.25 }}>
            <TextField size="small" label="Search Profile ID/Name/Email/Phone/Txn ID" value={search} onChange={(e) => setSearch(e.target.value)} />
            <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
            </TextField>
            <TextField size="small" type="number" label="Min Points" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
            <TextField size="small" type="number" label="Max Points" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
            <TextField size="small" label="Payment Method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} />
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                setStatusFilter('ALL');
                setMinAmount('');
                setMaxAmount('');
                setPaymentMethod('');
                setFromDate('');
                setToDate('');
              }}
              sx={{ borderColor: '#7b0011', color: '#7b0011' }}
            >
              Clear
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: '#7b0011' }} />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="info">No recharge records found for selected filters.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {items.map((item) => (
            <Card key={item.id} sx={{ borderRadius: 3, border: '1px solid #eddac2', boxShadow: '0 6px 18px rgba(78, 0, 11, 0.06)' }}>
              <CardContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.3fr 0.8fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: '#7b0011', fontWeight: 900 }}>{item.profile_id || '-'}</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#3a2b24' }}>{item.user_name || '-'}</Typography>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem', wordBreak: 'break-word' }}>{item.user_email || '-'}</Typography>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem' }}>{item.user_phone || '-'}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem' }}>Txn ID</Typography>
                  <Typography sx={{ fontFamily: 'monospace', color: '#3a2b24', wordBreak: 'break-word' }}>{item.transaction_id}</Typography>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem', mt: 1 }}>Payment Date</Typography>
                  <Typography sx={{ color: '#3a2b24' }}>{new Date(item.payment_date).toLocaleDateString('en-IN')}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem' }}>Amount</Typography>
                  <Typography sx={{ color: '#7b0011', fontWeight: 900, fontSize: '1.15rem' }}>{item.amount} Points</Typography>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem', mt: 1 }}>Method</Typography>
                  <Chip label={item.payment_method || '-'} size="small" variant="outlined" sx={{ mt: 0.8 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem' }}>Status</Typography>
                  <Chip size="small" label={item.status} color={getStatusColor(item.status)} sx={{ mt: 0.8 }} />
                  {item.rejection_reason && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.8, wordBreak: 'break-word' }}>
                      {item.rejection_reason}
                    </Typography>
                  )}
                  <Typography sx={{ color: '#7c6c63', fontSize: '0.82rem', mt: 1 }}>Created</Typography>
                  <Typography sx={{ color: '#3a2b24' }}>{new Date(item.created_at).toLocaleString('en-IN')}</Typography>
                </Box>
              </CardContent>
            </Card>
          ))}

          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Rows"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              sx={{ width: 120 }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </TextField>

            {hasNext && nextCursor && (
              <Button variant="outlined" disabled={loadingMore} onClick={() => fetchHistory(nextCursor, true)} sx={{ borderColor: '#7b0011', color: '#7b0011' }}>
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </AdminLayout>
  );
};

export default AdminRechargeHistoryPage;
